(function () {
  const selectors = {
    form: '[data-pb-drawer-predictive-search]',
    input: '.pb-menu-drawer-search__input',
    results: '[data-pb-drawer-search-results]',
  };

  const labels = {
    all: 'Caută toate rezultatele',
    empty: 'Nu am găsit încă potriviri rapide.',
    pages: 'Pagini utile',
    products: 'Produse recomandate',
    quick: 'Rezultate rapide',
  };

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function debounce(callback, wait) {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), wait);
    };
  }

  function formatPrice(value) {
    const price = Number(value);
    if (!Number.isFinite(price)) return '';

    return `${new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price)} lei`;
  }

  function getSuggestUrl(term) {
    const base = window.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(`${base}.json`, window.location.origin);

    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    return url;
  }

  function getSearchUrl(term) {
    const searchUrl = window.routes?.search_url || '/search';
    const url = new URL(searchUrl, window.location.origin);

    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');

    return `${url.pathname}${url.search}`;
  }

  function renderProduct(product) {
    const title = escapeHtml(product.title);
    const price = formatPrice(product.price);
    const image = product.image
      ? `<span class="pb-menu-drawer-search-result__media"><img src="${escapeHtml(product.image)}" alt="" loading="lazy"></span>`
      : '<span class="pb-menu-drawer-search-result__media pb-menu-drawer-search-result__media--empty">PB</span>';
    const meta = [product.type || 'Produs Pure Balance', price].filter(Boolean).join(' · ');

    return `
      <a class="pb-menu-drawer-search-result" href="${escapeHtml(product.url)}">
        ${image}
        <span class="pb-menu-drawer-search-result__copy">
          <span class="pb-menu-drawer-search-result__title">${title}</span>
          <span class="pb-menu-drawer-search-result__meta">${escapeHtml(meta)}</span>
        </span>
      </a>
    `;
  }

  function renderPage(page) {
    return `
      <a class="pb-menu-drawer-search-page" href="${escapeHtml(page.url)}">
        <span>${escapeHtml(page.title)}</span>
        <span aria-hidden="true">→</span>
      </a>
    `;
  }

  function renderResults(results, term) {
    const products = Array.isArray(results.products) ? results.products : [];
    const pages = Array.isArray(results.pages) ? results.pages : [];
    const visibleProducts = products.slice(0, 2);
    const visiblePages = visibleProducts.length < 2 ? pages.slice(0, 1) : [];
    const productMarkup = visibleProducts.map(renderProduct).join('');
    const pageMarkup = visiblePages.map(renderPage).join('');
    const fullSearch = getSearchUrl(term);
    const safeTerm = escapeHtml(term);

    if (!productMarkup && !pageMarkup) {
      return `
        <div class="pb-menu-drawer-search-results__empty">${labels.empty}</div>
        <a class="pb-menu-drawer-search-results__all" href="${fullSearch}">
          <span>${labels.all} pentru „${safeTerm}”</span>
          <span aria-hidden="true">→</span>
        </a>
      `;
    }

    return `
      <div class="pb-menu-drawer-search-results__eyebrow">${labels.quick}</div>
      ${productMarkup ? `<div class="pb-menu-drawer-search-results__group" aria-label="${labels.products}">${productMarkup}</div>` : ''}
      ${pageMarkup ? `<div class="pb-menu-drawer-search-results__pages" aria-label="${labels.pages}">${pageMarkup}</div>` : ''}
      <a class="pb-menu-drawer-search-results__all" href="${fullSearch}">
        <span>${labels.all} pentru „${safeTerm}”</span>
        <span aria-hidden="true">→</span>
      </a>
    `;
  }

  function setExpanded(input, expanded) {
    input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function hideResults(input, results) {
    results.hidden = true;
    results.innerHTML = '';
    results.classList.remove('is-loading');
    setExpanded(input, false);
  }

  function showLoading(input, results) {
    results.hidden = false;
    results.classList.add('is-loading');
    results.innerHTML = '<div class="pb-menu-drawer-search-results__empty">Căutăm recomandări...</div>';
    setExpanded(input, true);
  }

  function initPredictiveSearch(form) {
    const input = form.querySelector(selectors.input);
    const results = form.parentElement?.querySelector(selectors.results);
    if (!input || !results) return;

    let controller;

    const update = debounce(async () => {
      const term = input.value.trim();

      if (controller) controller.abort();
      if (term.length < 2) {
        hideResults(input, results);
        return;
      }

      controller = new AbortController();
      showLoading(input, results);

      try {
        const response = await fetch(getSuggestUrl(term), {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const data = await response.json();
        results.classList.remove('is-loading');
        results.innerHTML = renderResults(data.resources?.results || {}, term);
        results.hidden = false;
        setExpanded(input, true);
      } catch (error) {
        if (error.name === 'AbortError') return;
        hideResults(input, results);
      }
    }, 180);

    input.addEventListener('input', update);
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && results.innerHTML) {
        results.hidden = false;
        setExpanded(input, true);
      }
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hideResults(input, results);
    });

    const drawer = form.closest('details');
    drawer?.addEventListener('toggle', () => {
      if (!drawer.open) hideResults(input, results);
    });
  }

  function init() {
    document.querySelectorAll(selectors.form).forEach(initPredictiveSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
